import { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectDrillIn } from './useProjectDrillIn';
import { sparkToast } from '@spark';
import {
    deletedTasksToastTitle,
    displayTaskName,
    projectDeletedToastTitle,
    uniqueProjectTaskNames,
} from '@/features/tasks/utils/projectDeletedToast.jsx';
import { resolveProjectDeleteScope } from '@/features/tasks/utils/projectDeleteScope';
import {
    showBulkDeleteResultToast,
    showDeleteFailureToast,
} from '@/features/tasks/utils/taskDeleteToast';
import { createdByUserToSearchText, formatDateTime, includesSearchTerm, taskLanguagesToSearchText } from '@/shared/utils/helpers';
import { data_from_server_post } from '@/shared/http/client';
import { runBulkMove } from '@/features/tasks/utils/bulkMoveTasks';
import { scrollToProjectAnchor } from '@/shared/utils/scrollToProjectAnchor';
import { getTaskTypeLabel } from '@/shared/domain/taskTypes';
import {
    AGENTIC_FILTER_ALL,
    matchesAgenticFilter,
    matchesTaskTypeFilter,
    normalizeAgenticFilter,
} from '@/features/tasks/utils/taskTypeFilter';
import { GlobalContext } from '@/app/providers/globalState';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { matchesStatusFilter } from '@/features/tasks/utils/statusFilter';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { fetchHasAnySavedCredentials } from '@/features/settings/hooks/useOrganizationProviderSettings';

// Filter-bar state survives leaving the page (e.g. entering a task and
// coming back via the back button) within the tab session, so the user
// returns to the same filtered context. sessionStorage (not the URL) keeps
// shared/bookmarked links clean; the drilled-in project stays in `?project=`.
const FILTERS_STORAGE_KEY = 'homePageFilters';

const readStoredFilters = () => {
    try {
        return JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
};

export const useHomePage = () => {
    const navigate = useNavigate();
    // Drilled-in project is React state (synced to `?project=`). Back must
    // leave the view even when the address bar only has `usecaseId`.
    const {
        selectedProject,
        openProject,
        closeProject,
        renameDrilledProject,
    } = useProjectDrillIn();
    const [filterValue, setFilterValue] = useState(undefined);
    const [storedFilters] = useState(readStoredFilters);
    const [searchValue, setSearchValue] = useState(storedFilters.searchValue ?? "");
    const [filteredTaskType, setFilteredTaskType] = useState(storedFilters.taskType ?? "All");
    // "Agentic" toggle in the filter bar: `agentic` keeps only tool-calling tasks, `all` otherwise.
    const [filteredAgentic, setFilteredAgentic] = useState(
        () => normalizeAgenticFilter(storedFilters.agentic),
    );
    const [filteredUser, setFilteredUser] = useState(storedFilters.user ?? "All");
    const [filteredStatus, setFilteredStatus] = useState(storedFilters.status ?? "All");

    useEffect(() => {
        try {
            sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
                searchValue,
                taskType: filteredTaskType,
                agentic: filteredAgentic,
                user: filteredUser,
                status: filteredStatus,
            }));
        } catch (e) {
            // Persisting filters is best-effort only.
        }
    }, [searchValue, filteredTaskType, filteredAgentic, filteredUser, filteredStatus]);
    const [projectCreatedBar, setProjectCreatedBar] = useState({ visible: false, projectName: '', usecaseId: null, isProjectCreation: false });
    const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
    const [currentProjectData, setCurrentProjectData] = useState(null);
    const [editProjectLoading, setEditProjectLoading] = useState(false);
    const [isDuplicateTaskModalOpen, setIsDuplicateTaskModalOpen] = useState(false);
    const [taskToDuplicate, setTaskToDuplicate] = useState(null);
    const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState(null);
    // 'umbrella' edits the whole task (atomic: usecase_name + sub_group_name;
    // multi: shared sub_group_name across subtasks). 'subtask' edits only the
    // passed usecase's `usecase_name` (multi-task subtask rename).
    const [editTaskMode, setEditTaskMode] = useState('umbrella');
    const [isProvidersOnboardingOpen, setIsProvidersOnboardingOpen] = useState(false);

    const {
        allApps,
        refreshUsecases,
        allUsecases,
        updateStatus,
        isFetchingUsecases,
        viewType,
        setViewTypeState,
    } = useContext(GlobalContext);

    const { availableProviders } = useCommercialModels();
    const hasProviderCredentials = Object.keys(availableProviders || {}).length > 0;

    // Extract unique users from all tasks
    const allUsers = useMemo(() => {
        const usersMap = new Map();
        allApps.forEach(app => {
            (app.sub_groups || []).forEach(subGroup => {
                (subGroup.usecases || []).forEach(usecase => {
                    if (usecase.created_by_user) {
                        const userId = usecase.created_by_user.sub || usecase.created_by_user.email;
                        const userName = usecase.created_by_user.name || usecase.created_by_user.email || usecase.created_by_user.sub || 'Unknown';
                        if (userId && !usersMap.has(userId)) {
                            usersMap.set(userId, {
                                id: userId,
                                name: userName,
                                user: usecase.created_by_user
                            });
                        }
                    }
                });
            });
        });
        return Array.from(usersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allApps]);

    const filteredApps = useMemo(() => {
        let apps = allApps;
        if (filterValue) {
            apps = apps.filter(app => app.group_name === filterValue);
        }
        // Apply unified search - search both project names and task names
        const hasAgenticScope = filteredAgentic !== AGENTIC_FILTER_ALL;
        if (searchValue || filteredTaskType !== "All" || hasAgenticScope || filteredUser !== "All" || filteredStatus !== "All") {
            const getUsecaseSearchParts = (usecase) => {
                if (!usecase) return [];
                const taskTypeLabel = getTaskTypeLabel(usecase.task_type, usecase.is_multilabel, usecase.classify_speakers);
                const lastUpdated = usecase.last_update_time ? formatDateTime(usecase.last_update_time) : '';
                const createdBy = createdByUserToSearchText(usecase.created_by_user);
                const languages = taskLanguagesToSearchText(usecase.task_languages);
                return [
                    usecase.usecase_name,
                    // For composed tasks the displayed title is the shared `sub_group_name`
                    // (e.g. "blue-ocean"), not the per-objective `usecase_name`.
                    usecase.sub_group_name,
                    usecase.task_type,
                    taskTypeLabel,
                    createdBy,
                    lastUpdated,
                    languages,
                ];
            };

            const usecaseMatchesSearch = (usecase) => {
                return includesSearchTerm(searchValue, getUsecaseSearchParts(usecase));
            };

            const usecaseMatchesTaskType = (usecase) => (
                matchesTaskTypeFilter(usecase, filteredTaskType)
                && matchesAgenticFilter(usecase, filteredAgentic)
            );

            const usecaseMatchesUser = (usecase) => {
                const userId = usecase.created_by_user?.sub || usecase.created_by_user?.email;
                return filteredUser === "All" ||
                    (Array.isArray(filteredUser) ? filteredUser.includes(userId) : userId === filteredUser);
            };

            const usecaseMatchesStatus = (usecase) => (
                matchesStatusFilter(usecase, filteredStatus)
            );

            const usecaseMatchesAllCriteria = (usecase) => {
                return usecaseMatchesSearch(usecase) && usecaseMatchesTaskType(usecase) && usecaseMatchesUser(usecase) && usecaseMatchesStatus(usecase);
            };

            apps = apps.map(app => {
                const hasSearch = !!searchValue?.trim();
                const projectNameMatches = hasSearch && includesSearchTerm(searchValue, app.group_name);

                const processedSubGroups = (app.sub_groups || []).map(subGroup => {
                    const originalUsecases = subGroup.usecases || [];
                    // A composed task is rendered under its `sub_group_name` (e.g. "blue-ocean").
                    // When the search matches that parent task name, surface the whole task —
                    // its individual objectives' `usecase_name`s (e.g. "classification1") would
                    // otherwise fail the per-usecase search.
                    const subGroupNameMatches = hasSearch && includesSearchTerm(searchValue, subGroup.sub_group_name);
                    const parentMatches = projectNameMatches || subGroupNameMatches;

                    const filteredUsecases = originalUsecases.filter(usecase => {
                        const taskTypeMatches = usecaseMatchesTaskType(usecase);
                        const userMatches = usecaseMatchesUser(usecase);
                        const statusMatches = usecaseMatchesStatus(usecase);

                        // When the project or parent task name matches, skip the per-usecase
                        // search check and only apply the non-search filters. Otherwise, every
                        // criterion (including search) must match.
                        if (parentMatches) {
                            return taskTypeMatches && userMatches && statusMatches;
                        }
                        return usecaseMatchesAllCriteria(usecase);
                    });

                    // A composed task is a single conceptual unit — when any of its
                    // sub-tasks matches the active filters (search/status/task-type/user),
                    // show the entire composed task with all sibling sub-tasks attached
                    // instead of stripping it down to only the matches. This avoids the
                    // misleading "card with one sub-task" view when searching by an
                    // individual `usecase_name` like "one-class".
                    const isComposedTask = originalUsecases.length > 1;
                    const keepComposedTaskIntact = isComposedTask && filteredUsecases.length > 0;

                    return {
                        ...subGroup,
                        usecases: keepComposedTaskIntact ? originalUsecases : filteredUsecases,
                    };
                }).filter(subGroup => subGroup.usecases.length > 0);

                if (processedSubGroups.length === 0) {
                    return null;
                }

                return {
                    ...app,
                    sub_groups: processedSubGroups,
                };
            }).filter(app => app !== null);

        }

        return apps;
    }, [allApps, filterValue, filteredTaskType, filteredAgentic, filteredUser, filteredStatus, searchValue]);

    const handleDeleteUsecase = async (client_id, usecase_id) => {
        try {
            const usecase = allUsecases.find((u) => u._id === usecase_id);
            const taskName = usecase?.usecase_name || displayTaskName(usecase);
            data_from_server_post("delete_llm_usecase", {
                client_id,
                usecase_id,
            },
                (result) => {
                    if (result.success) {
                        sparkToast.success(deletedTasksToastTitle([taskName].filter(Boolean)));
                        refreshUsecases();
                    } else {
                        showDeleteFailureToast(result.message, 'Failed to delete task');
                    }
                }
            );
        } catch (error) {
            sparkToast.error("Failed to delete task");
        }
    };

    const handleBulkDeleteUsecases = async (usecases = [], { notify = true } = {}) => {
        try {
            if (!Array.isArray(usecases) || usecases.length === 0) {
                return { success: false, message: "No tasks selected for deletion" };
            }

            // Group by client_id to keep backend assumptions safe
            const byClient = new Map();
            usecases.forEach((u) => {
                if (!u?.client_id || !u?._id) return;
                if (!byClient.has(u.client_id)) byClient.set(u.client_id, []);
                byClient.get(u.client_id).push(u._id);
            });

            const results = await Promise.all(
                Array.from(byClient.entries()).map(([client_id, ids]) => (
                    data_from_server_post("bulk-delete-usecases", {
                        client_id,
                        usecase_ids: ids,
                    })
                ))
            );

            const deleted = results.reduce((acc, r) => acc + (r?.deleted_count || 0), 0);
            const failed = results.reduce((acc, r) => acc + (r?.failed_count || 0), 0);
            const failures = results.flatMap((r) => r?.failures || []);

            if (notify) {
                showBulkDeleteResultToast({ deleted, failed, failures, usecases });
            }

            refreshUsecases();
            return { success: deleted > 0, deleted_count: deleted, failed_count: failed, failures };
        } catch (error) {
            sparkToast.error("Failed to delete tasks");
            return { success: false, message: "Failed to delete tasks" };
        }
    };

    const handleBulkMoveUsecases = async (usecases = [], destGroupName) => {
        try {
            const { success, moved, failures } = await runBulkMove(
                usecases,
                destGroupName,
                allUsecases,
                data_from_server_post
            );
            if (failures[0] === 'No tasks selected to move') {
                return { success: false, message: failures[0] };
            }

            refreshUsecases();

            if (success) {
                sparkToast.success(
                    `Moved ${usecases.length} item${usecases.length === 1 ? '' : 's'} to ${destGroupName}`
                );
                return { success: true, moved_count: moved };
            }

            sparkToast.error(failures[0]);
            return { success: moved > 0, moved_count: moved, failed_count: failures.length };
        } catch (error) {
            sparkToast.error('Failed to move tasks');
            return { success: false, message: 'Failed to move tasks' };
        }
    };

    const handleDeleteProject = async (app) => {
        const taskNames = uniqueProjectTaskNames(app);
        const toastOptions = { duration: taskNames.length > 1 ? 6000 : 4000 };

        try {
            const scope = resolveProjectDeleteScope(app, allApps);
            if (scope.mode === 'filtered-tasks') {
                const result = await handleBulkDeleteUsecases(scope.usecases, { notify: false });
                if (result.success && !(result.failed_count > 0)) {
                    sparkToast.success(deletedTasksToastTitle(taskNames), toastOptions);
                } else {
                    showBulkDeleteResultToast({
                        deleted: result.deleted_count,
                        failed: result.failed_count,
                        failures: result.failures,
                        usecases: scope.usecases,
                    });
                }
                return { ...result, projectDeleted: false };
            }

            const result = await data_from_server_post("delete_project", {
                client_id: "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                group_name: app.group_name,
            });
            if (result?.success) {
                const namesFromApi = Array.isArray(result.deleted_task_names)
                    ? result.deleted_task_names
                    : [];
                const names = taskNames.length > 0 ? taskNames : namesFromApi;
                sparkToast.success(
                    names.length > 0
                        ? projectDeletedToastTitle(
                            result.project_name || app.group_name,
                            names
                        )
                        : result.message,
                    toastOptions
                );
                refreshUsecases();
                return { success: true, projectDeleted: true };
            }
            showDeleteFailureToast(result?.message, 'Failed to delete project');
            return { success: false, projectDeleted: false };
        } catch (error) {
            sparkToast.error("Failed to delete project");
            return { success: false, projectDeleted: false };
        }
    };

    const handleEditProject = async (app) => {
        setCurrentProjectData(app);
        setIsEditProjectModalOpen(true);
    };

    const handleSaveEditedProject = async (editedProjectData) => {
        setEditProjectLoading(true);
        try {
            data_from_server_post("edit_project", {
                client_id: "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                original_group_name: currentProjectData.group_name,
                group_name: editedProjectData.group_name,
                project_goal: editedProjectData.project_goal,
            },
                (result) => {
                    if (result.success) {
                        sparkToast.success("Project updated successfully!");
                        // If the renamed project is the drilled-in one, point the
                        // `?project=` param at the new name so the view keeps
                        // showing its tasks instead of a no-results state.
                        if (
                            selectedProject === currentProjectData.group_name &&
                            editedProjectData.group_name !== currentProjectData.group_name
                        ) {
                            renameDrilledProject(editedProjectData.group_name);
                        }
                        refreshUsecases();
                        setIsEditProjectModalOpen(false);
                        setCurrentProjectData(null);
                    } else {
                        sparkToast.error(result.message || "Failed to update project");
                    }
                    setEditProjectLoading(false);
                }
            );
        } catch (error) {
            sparkToast.error("Failed to update project");
            setEditProjectLoading(false);
        }
    };

    const handleModelDeployment = async (usecase_id, action, version = null) => {
        try {
            const usecase = allUsecases.find(u => u._id === usecase_id);
            if (!usecase) {
                sparkToast.error("Usecase not found");
                return;
            }

            const statusUpdate = action === "deploy" ? deploy_in_progress : undeploy_in_progress;
            updateStatus(usecase_id, statusUpdate);

            // If deploying and version is explicitly provided, use it; otherwise use the latest version
            // Check for both null and undefined, but allow 0 as a valid version number
            const deployed_version = action === "deploy"
                ? (version !== null && version !== undefined ? Number(version) : usecase.version)
                : usecase.deployed_version;

            data_from_server_post(
                "model-deployment",
                {
                    client_id: usecase.client_id,
                    llm_usecase_id: usecase_id,
                    task_type: usecase.task_type,
                    request_type: action,
                    deployed_version: deployed_version,
                },
                (result) => {
                    if (result.success) {
                        sparkToast.success(`${action} in progress`);
                        setTimeout(refreshUsecases, 2000);
                    } else {
                        sparkToast.error(`Failed to ${action} model`);
                        refreshUsecases();
                    }
                }
            );
        } catch (error) {
            sparkToast.error(`Failed to ${action} model`);
            refreshUsecases();
        }
    };

    const handleDuplicateUsecase = (originalTask) => {
        setTaskToDuplicate(originalTask);
        setIsDuplicateTaskModalOpen(true);
    };

    const handleEditTask = (task, mode = 'umbrella') => {
        setTaskToEdit(task);
        setEditTaskMode(mode === 'subtask' ? 'subtask' : 'umbrella');
        setIsEditTaskModalOpen(true);
    };

    const handleUpdateTask = (updatedTaskData) => {
        if (!taskToEdit) return;

        data_from_server_post(
            "update_llm_usecase",
            {
                usecase_id: taskToEdit._id,
                client_id: taskToEdit.client_id,
                usecase_name: updatedTaskData.usecase_name,
                usecase_goal: updatedTaskData.usecase_goal,
                task_type: updatedTaskData.task_type,
                group_name: updatedTaskData.group_name,
                sub_group_name: updatedTaskData.sub_group_name,
                // When editing the umbrella of a multi-task, the backend
                // renames `sub_group_name` across every sibling so the whole
                // Task stays bundled together.
                propagate_sub_group_name: !!updatedTaskData.propagate_sub_group_name,
                is_multilabel: updatedTaskData.is_multilabel || false,
                classify_speakers: updatedTaskData.classify_speakers || false,
                confidence_rejection_enabled:
                    updatedTaskData.confidence_rejection_enabled !== false,
                // Single-label classification "Other"/rejection fallback label.
                // Backend ignores it for other task types.
                rejection_label_name: updatedTaskData.rejection_label_name ?? null,
                task_languages: updatedTaskData.task_languages || [],
                // Business goals fields — only sent when the modal included
                // them so partial edits don't clobber existing values.
                ...(updatedTaskData.business_goal_priorities !== undefined && {
                    business_goal_priorities: updatedTaskData.business_goal_priorities,
                }),
                ...(updatedTaskData.constraints !== undefined && {
                    constraints: updatedTaskData.constraints,
                }),
                ...(updatedTaskData.manually_added_labels !== undefined && {
                    manually_added_labels: updatedTaskData.manually_added_labels,
                }),
                ...(updatedTaskData.manually_added_entities !== undefined && {
                    manually_added_entities: updatedTaskData.manually_added_entities,
                }),
                // "What matters most for these labels?" — null lets the
                // backend keep (or auto-clear) the stored choice.
                ...(updatedTaskData.label_priority !== undefined && {
                    label_priority: updatedTaskData.label_priority,
                }),
            },
            (result) => {
                if (result.success) {
                    sparkToast.success("Task updated successfully!");
                    refreshUsecases();
                    setIsEditTaskModalOpen(false);
                    setTaskToEdit(null);
                    setEditTaskMode('umbrella');
                } else {
                    sparkToast.error(result.message || "Error updating task!");
                }
            }
        );
    };

    const handleCreateDuplicateTask = (duplicatedTaskData) => {
        const duplicatedTasks = Array.isArray(duplicatedTaskData)
            ? duplicatedTaskData
            : [duplicatedTaskData];
        const firstDuplicatedTask = duplicatedTasks[0];
        const duplicatedTaskName =
            firstDuplicatedTask?.sub_group_name || firstDuplicatedTask?.usecase_name;

        if (!firstDuplicatedTask || !duplicatedTaskName) {
            sparkToast.error("No task selected for duplication");
            return;
        }

        data_from_server_post(
            "create_llm_usecase",
            {
                tasks: duplicatedTasks,
                group_name: firstDuplicatedTask.group_name,
                client_id: "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
            },
            (result) => {
                if (result.success) {
                    refreshUsecases((allUsecases) => {
                        const newUsecase = allUsecases.find(u =>
                            u.sub_group_name === duplicatedTaskName ||
                            u.usecase_name === duplicatedTaskName
                        );
                        setProjectCreatedBar({
                            visible: true,
                            projectName: firstDuplicatedTask.group_name,
                            taskName: duplicatedTaskName,
                            usecaseId: newUsecase ? newUsecase._id : null,
                            isProjectCreation: false
                        });
                        setIsDuplicateTaskModalOpen(false);
                        setTaskToDuplicate(null);
                    });
                } else {
                    sparkToast.error(result?.message || "Error. Task with this name may already exist!");
                }
            }
        );
    };

    // Both create entry points open the full-page creation wizard
    // (CreateTaskWizardPage) — the legacy Create Project / New Task modals
    // are retired. When no model-provider credentials exist, gate through the
    // first-time Model Providers onboarding modal first.
    const goToCreateTask = useCallback(() => {
        navigate('/create-task');
    }, [navigate]);

    const openCreateProjectModal = useCallback(() => {
        // A non-empty models catalog proves credentials exist, but an empty
        // one does not prove they are missing (fetch may be in flight, have
        // failed, or the catalog may lag behind a freshly saved key). Before
        // gating, confirm against the org settings — the source of truth.
        if (hasProviderCredentials) {
            goToCreateTask();
            return;
        }
        fetchHasAnySavedCredentials((hasSavedCredentials) => {
            if (hasSavedCredentials) {
                goToCreateTask();
            } else {
                setIsProvidersOnboardingOpen(true);
            }
        });
    }, [goToCreateTask, hasProviderCredentials]);

    const closeProvidersOnboardingModal = useCallback(() => {
        setIsProvidersOnboardingOpen(false);
    }, []);

    const continueFromProvidersOnboarding = useCallback(() => {
        setIsProvidersOnboardingOpen(false);
        goToCreateTask();
    }, [goToCreateTask]);

    const openNewTaskModal = (projectName) => {
        navigate(
            projectName
                ? `/create-task?project=${encodeURIComponent(projectName)}`
                : '/create-task'
        );
    };

    const closeEditProjectModal = () => {
        setIsEditProjectModalOpen(false);
        setCurrentProjectData(null);
    };

    const closeDuplicateTaskModal = () => {
        setIsDuplicateTaskModalOpen(false);
        setTaskToDuplicate(null);
    };

    const closeEditTaskModal = () => {
        setIsEditTaskModalOpen(false);
        setTaskToEdit(null);
        setEditTaskMode('umbrella');
    };

    const closeProjectCreatedBar = () => setProjectCreatedBar({ visible: false, projectName: '', usecaseId: null, isProjectCreation: false });

    const handleViewProject = (projectName) => {
        // Close the notification bar
        closeProjectCreatedBar();

        // Cards view shows project summary cards only — drill into the
        // project so its tasks become visible.
        if (viewType === 'cards') {
            openProject(projectName);
            return;
        }

        // Ensure expansion in List view
        try {
            const evt = new CustomEvent('home:expand-project', { detail: { projectName } });
            window.dispatchEvent(evt);
        } catch (e) {
            // no-op if CustomEvent not supported
        }

        // Scroll to the project anchor (works in both Cards and List views).
        // Defer to let the bar close and the list expand render first.
        setTimeout(() => {
            scrollToProjectAnchor(projectName);
        }, 50);
    };

    // Load user preference for default view on mount
    useEffect(() => {
        try {
            data_from_server_post("users/bootstrap", {}, (res) => {
                const pref = res?.user?.preferences?.home_default_view;
                if (pref === 'cards' || pref === 'list') {
                    setViewTypeState(pref);
                }
            });
        } catch (e) {
            // ignore
        }
    }, []);

    const computedHomeLoading = isFetchingUsecases;

    return {
        // State
        viewType,
        selectedProject,
        filterValue,
        searchValue,
        taskType: filteredTaskType,
        agenticFilter: filteredAgentic,
        userFilter: filteredUser,
        statusFilter: filteredStatus,
        allUsers,
        allApps,
        filteredApps,
        allUsecases,
        projectCreatedBar,
        isEditProjectModalOpen,
        currentProjectData,
        editProjectLoading,
        isDuplicateTaskModalOpen,
        taskToDuplicate,
        isEditTaskModalOpen,
        taskToEdit,
        editTaskMode,
        isProvidersOnboardingOpen,

        // Actions
        setViewType: (newType) => {
            const normalized = newType === 'list' ? 'list' : 'cards';
            setViewTypeState(normalized);
            try {
                data_from_server_post(
                    "users/set-preferences",
                    normalized,
                    () => { },
                    () => { }
                );
            } catch (e) {
                // ignore persist errors
            }
        },
        setFilterValue,
        setSearchValue,
        setTaskType: setFilteredTaskType,
        setAgenticFilter: (mode) => setFilteredAgentic(normalizeAgenticFilter(mode)),
        setUserFilter: setFilteredUser,
        setStatusFilter: setFilteredStatus,
        openCreateProjectModal,
        openNewTaskModal,
        openProject,
        closeProject,
        closeProvidersOnboardingModal,
        continueFromProvidersOnboarding,
        closeEditProjectModal,
        closeDuplicateTaskModal,
        closeEditTaskModal,
        handleDeleteUsecase,
        handleBulkDeleteUsecases,
        handleBulkMoveUsecases,
        handleDeleteProject,
        handleEditProject,
        handleSaveEditedProject,
        handleModelDeployment,
        handleDuplicateUsecase,
        handleCreateDuplicateTask,
        handleEditTask,
        handleUpdateTask,
        closeProjectCreatedBar,
        handleViewProject,
        homeLoading: computedHomeLoading,
    };
}; 